import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, LogOut, User as UserIcon, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", { name });
      await refreshUser();
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 animate-fade-up">
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Settings</h1>
      <p className="mt-1 text-slate-500">Manage your profile and account.</p>

      <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-primary text-lg font-semibold text-white">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}
          </div>
          <div>
            <div className="font-heading text-lg font-semibold">{user?.name}</div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Mail className="h-3.5 w-3.5" /> {user?.email}
            </div>
          </div>
          <Badge variant="secondary" className="ml-auto gap-1 capitalize">
            {user?.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />} {user?.role}
          </Badge>
        </div>

        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Display name</Label>
            <Input id="name" data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="h-11 bg-slate-50" />
          </div>
          <div className="space-y-1.5">
            <Label>Sign-in method</Label>
            <Input value={user?.auth_provider === "google" ? "Google" : "Email & password"} disabled className="h-11 bg-slate-50 capitalize" />
          </div>
          <Button onClick={save} disabled={saving} className="h-11 font-medium" data-testid="save-profile-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-lg font-semibold tracking-tight">Account</h2>
        <p className="mt-1 text-sm text-slate-500">Log out of your CanHelp Move account on this device.</p>
        <Button variant="outline" onClick={handleLogout} className="mt-4 gap-2" data-testid="settings-logout-btn">
          <LogOut className="h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}
